import {
  Directive,
  ElementRef,
  HostListener,
  Injector,
  Input,
  forwardRef,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  NG_VALIDATORS,
  NgControl,
  ValidationErrors,
  Validator,
} from '@angular/forms';

export type NumericKind = 'integer' | 'decimal';

/** The number a numeric input holds, or null when it's empty, not a number yet, or not text at all. */
export function parseNumericInput(value: unknown): number | null {
  const trimmed = typeof value === 'string' ? value.trim() : '';
  const n = trimmed === '' ? NaN : Number(trimmed.replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

/**
 * Keeps a text input to a number as typed: digits, a leading minus and, for decimals, one `.` or `,`
 * separator. The value stays a string. Anything else is dropped as it is typed or pasted, and leftovers
 * that are not a number yet (`-`, `1,`) are tidied away on blur; values set from code are only
 * validated, failing with a `numeric` error.
 */
@Directive({
  selector: 'input[foNumericInput]',
  host: {
    '[attr.inputmode]': "kind === 'decimal' ? 'decimal' : 'numeric'",
    autocomplete: 'off',
  },
  providers: [
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => NumericInputDirective),
      multi: true,
    },
  ],
})
export class NumericInputDirective implements Validator {
  @Input({ alias: 'foNumericInput', required: true }) kind: NumericKind;

  private input: HTMLInputElement = inject(ElementRef).nativeElement;
  private injector = inject(Injector);

  // Looked up on use: injecting NgControl eagerly is circular, since NgControl itself injects this
  // directive as one of its validators.
  private get control(): AbstractControl | null {
    return this.injector.get(NgControl, null, { self: true })?.control ?? null;
  }

  @HostListener('input')
  protected onInput(): void {
    const { value, selectionStart } = this.input;
    const kept = this.sanitize(value);
    if (kept === value) {
      return;
    }
    const caret =
      selectionStart == null
        ? kept.length
        : this.sanitize(value.slice(0, selectionStart)).length;
    this.input.value = kept;
    this.input.setSelectionRange(caret, caret);
    this.control?.setValue(kept);
  }

  @HostListener('blur')
  protected onBlur(): void {
    const value = this.input.value;
    const tidied = value
      .replace(/^(-?)([.,])/, (_, sign, sep) => `${sign}0${sep}`)
      .replace(/[.,]$/, '')
      .replace(/^-$/, '')
      .replace(/^-(0([.,]0*)?)$/, '$1');
    if (tidied !== value) {
      this.control?.setValue(tidied);
    }
  }

  validate(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (value == null || value === '') {
      return null;
    }
    const pattern = this.kind === 'decimal' ? /^-?\d+([.,]\d+)?$/ : /^-?\d+$/;
    return pattern.test(String(value)) ? null : { numeric: true };
  }

  private sanitize(value: string): string {
    let out = '';
    let separatorSeen = false;
    for (const ch of value) {
      if (ch >= '0' && ch <= '9') {
        out += ch;
      } else if (ch === '-' && out === '') {
        out += ch;
      } else if (
        (ch === '.' || ch === ',') &&
        this.kind === 'decimal' &&
        !separatorSeen
      ) {
        separatorSeen = true;
        out += ch;
      }
    }
    return out;
  }
}
